import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  Alert,
  Box,
  Button,
  Grid,
  IconButton,
  Skeleton,
  Tooltip,
  Typography,
} from '@mui/material';

import type { TimelineSummary } from '../services';

interface TimelineKpisProps {
  summary?: TimelineSummary;
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
}

const summaryMetrics: Array<{
  key: keyof TimelineSummary;
  label: string;
  caption: string;
}> = [
  {
    key: 'records',
    label: 'Registros',
    caption: 'documentos na base',
  },
  {
    key: 'analyzed',
    label: 'Com análise',
    caption: 'com resumo e contexto',
  },
  {
    key: 'highMateriality',
    label: 'Alta materialidade',
    caption: 'prioridade de leitura',
  },
  {
    key: 'citations',
    label: 'Citações',
    caption: 'fontes rastreadas',
  },
];

const formatCount = (count?: number) =>
  typeof count === 'number' && Number.isFinite(count)
    ? new Intl.NumberFormat('pt-BR').format(count)
    : '—';

const getMetricDividerSx = (index: number) => ({
  borderRight: {
    xs: 'none',
    sm: index % 2 === 0 ? '1px solid' : 'none',
    md: index < summaryMetrics.length - 1 ? '1px solid' : 'none',
  },
  borderRightColor: {
    xs: 'divider',
    sm: index === 0 ? 'primary.main' : 'divider',
    md: index === 0 ? 'primary.main' : 'divider',
  },
  borderBottom: {
    xs: index < summaryMetrics.length - 1 ? '1px solid' : 'none',
    sm: index < 2 ? '1px solid' : 'none',
    md: 'none',
  },
  borderBottomColor: 'divider',
});

export default function TimelineKpis({
  summary,
  isLoading,
  isError,
  onRetry,
}: TimelineKpisProps) {
  return (
    <Box
      component="section"
      data-testid="timeline-kpis"
      aria-label="Resumo da base"
      sx={{
        p: { xs: 1.5, md: 2 },
        bgcolor: 'grey.50',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
      }}>
      {isError ? (
        <Alert
          data-testid="timeline-summary-error"
          severity="error"
          sx={{ mt: 1 }}
          action={
            <Button color="inherit" size="small" onClick={onRetry}>
              Tentar novamente
            </Button>
          }>
          Não foi possível carregar o resumo da base. Tente novamente em
          instantes.
        </Alert>
      ) : (
        <Grid container sx={{ mt: 1 }}>
          {summaryMetrics.map((metric, index) => (
            <Grid
              item
              key={metric.key}
              xs={12}
              sm={6}
              md={3}
              sx={getMetricDividerSx(index)}>
              <Box
                sx={{
                  height: '100%',
                  px: { xs: 0.5, md: 2 },
                  py: { xs: 1.25, md: 0.75 },
                }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    fontWeight={600}
                    sx={{ lineHeight: 1.2 }}>
                    {metric.label}
                  </Typography>
                  {metric.key === 'highMateriality' && (
                    <Tooltip title="Nota calculada que combina a análise do documento com a relevância informada. Não é o mesmo que o filtro de Relevância Alta.">
                      <IconButton
                        size="small"
                        aria-label="Sobre Alta materialidade"
                        sx={{ ml: 0.5, p: 0.25 }}>
                        <InfoOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
                {isLoading ? (
                  <Skeleton
                    data-testid={`timeline-summary-${metric.key}-loading`}
                    variant="text"
                    width="56%"
                    height={36}
                    sx={{ mt: 0.5 }}
                  />
                ) : (
                  <Typography
                    data-testid={`timeline-summary-${metric.key}`}
                    variant="h5"
                    fontWeight={700}
                    color={
                      metric.key === 'analyzed'
                        ? 'primary.main'
                        : 'text.primary'
                    }
                    sx={{
                      lineHeight: 1.2,
                      mt: 0.5,
                      fontVariantNumeric: 'tabular-nums',
                    }}>
                    {formatCount(summary?.[metric.key])}
                  </Typography>
                )}
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'block', mt: 0.25 }}>
                  {metric.caption}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
}
